import { useState } from "react";
import { toast } from "sonner";
import { apiClient } from "@/App";
import { MailCheck, MapPin, Plus, X, Building2, Video, HardHat, MapPinned } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const OFFICE_ADDRESS = "2nd Floor, Mesco Builders";
const OFFICE_MAP_URL = "https://maps.app.goo.gl/fMQZhc5pJXLmxxgj8";

const emptyForm = {
  title: "",
  meeting_with: "Ar. Jerin",
  scheduled_date: "",
  scheduled_time: "",
  duration_minutes: 60,
  meeting_type: "office",
  location: "",
  notes: ""
};

export const ConfirmMeetingModal = ({ open, onOpenChange }) => {
  const [form, setForm] = useState(emptyForm);
  const [emails, setEmails] = useState([]);
  const [emailInput, setEmailInput] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const addEmail = () => {
    const val = emailInput.trim().toLowerCase();
    if (!val) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) {
      toast.error("Invalid email address");
      return;
    }
    if (emails.includes(val)) {
      toast.error("Email already added");
      return;
    }
    setEmails([...emails, val]);
    setEmailInput("");
  };

  const handleSubmit = async () => {
    const pendingEmail = emailInput.trim();
    let allEmails = emails;
    if (pendingEmail && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(pendingEmail) && !emails.includes(pendingEmail.toLowerCase())) {
      allEmails = [...emails, pendingEmail.toLowerCase()];
    }
    if (!form.title.trim() || !form.scheduled_date || !form.scheduled_time) {
      toast.error("Please fill purpose, date and time");
      return;
    }
    if (allEmails.length === 0) {
      toast.error("Add at least one email address");
      return;
    }
    setSubmitting(true);
    try {
      const res = await apiClient.post("/meeting-confirmation", {
        ...form,
        duration_minutes: parseInt(form.duration_minutes) || 60,
        location: form.meeting_type === "office" ? null : form.location || null,
        notes: form.notes || null,
        emails: allEmails
      });
      const sentCount = res.data.sent?.length || 0;
      const failedCount = res.data.failed?.length || 0;
      if (failedCount > 0) {
        toast.warning(`Sent to ${sentCount}, failed for ${failedCount}: ${res.data.failed.join(", ")}`);
      } else {
        toast.success(`Confirmation email sent to ${sentCount} recipient${sentCount > 1 ? "s" : ""}!`);
      }
      setForm(emptyForm);
      setEmails([]);
      setEmailInput("");
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send confirmation");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#1A1A1A] border-[#262626] w-[95vw] max-sm:w-full max-w-xl max-h-[85dvh] overflow-y-auto mx-auto">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <MailCheck className="w-5 h-5 text-[#FF6B35]" /> Meeting Confirmation Email
          </DialogTitle>
          <DialogDescription className="text-[#888]">
            Fixed a meeting verbally or over the phone? Send everyone a classy confirmation email with the details.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="text-gray-400">Meeting Purpose / Title *</Label>
            <Input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="e.g., Initial Design Discussion"
              className="bg-[#111111] border-[#262626]"
              data-testid="confirm-meeting-title"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-gray-400">Date *</Label>
              <Input type="date" value={form.scheduled_date} onChange={(e) => setForm({ ...form, scheduled_date: e.target.value })} className="bg-[#111111] border-[#262626]" data-testid="confirm-meeting-date" />
            </div>
            <div>
              <Label className="text-gray-400">Time *</Label>
              <Input type="time" value={form.scheduled_time} onChange={(e) => setForm({ ...form, scheduled_time: e.target.value })} className="bg-[#111111] border-[#262626]" data-testid="confirm-meeting-time" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-gray-400">Meeting With</Label>
              <Input value={form.meeting_with} onChange={(e) => setForm({ ...form, meeting_with: e.target.value })} className="bg-[#111111] border-[#262626]" />
            </div>
            <div>
              <Label className="text-gray-400">Duration (minutes)</Label>
              <Select value={String(form.duration_minutes)} onValueChange={(v) => setForm({ ...form, duration_minutes: v })}>
                <SelectTrigger className="bg-[#111111] border-[#262626]"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-[#262626]">
                  <SelectItem value="30">30 minutes</SelectItem>
                  <SelectItem value="45">45 minutes</SelectItem>
                  <SelectItem value="60">1 hour</SelectItem>
                  <SelectItem value="90">1.5 hours</SelectItem>
                  <SelectItem value="120">2 hours</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-gray-400">Meeting Type *</Label>
            <Select value={form.meeting_type} onValueChange={(v) => setForm({ ...form, meeting_type: v })}>
              <SelectTrigger className="bg-[#111111] border-[#262626]" data-testid="confirm-meeting-type"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#1A1A1A] border-[#262626]">
                <SelectItem value="office"><span className="flex items-center gap-2"><Building2 className="w-3.5 h-3.5" /> Office Meeting</span></SelectItem>
                <SelectItem value="online"><span className="flex items-center gap-2"><Video className="w-3.5 h-3.5" /> Online Call</span></SelectItem>
                <SelectItem value="site"><span className="flex items-center gap-2"><HardHat className="w-3.5 h-3.5" /> Site Visit</span></SelectItem>
                <SelectItem value="other"><span className="flex items-center gap-2"><MapPinned className="w-3.5 h-3.5" /> Other Location</span></SelectItem>
              </SelectContent>
            </Select>
          </div>

          {form.meeting_type === "office" ? (
            <div className="flex items-start gap-3 p-3 bg-[#111111] rounded-lg border border-[#FF6B35]/30" data-testid="office-address-preview">
              <MapPin className="w-4 h-4 text-[#FF6B35] mt-0.5 shrink-0" />
              <div>
                <p className="text-sm text-white">{OFFICE_ADDRESS}</p>
                <p className="text-xs text-gray-500 mt-0.5">Google Maps link will be included in the email automatically</p>
                <a href={OFFICE_MAP_URL} target="_blank" rel="noreferrer" className="text-xs text-[#FF6B35] hover:underline">Preview map →</a>
              </div>
            </div>
          ) : (form.meeting_type === "site" || form.meeting_type === "other") && (
            <div>
              <Label className="text-gray-400">Location</Label>
              <Input
                value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
                placeholder="e.g., Site address or venue"
                className="bg-[#111111] border-[#262626]"
              />
            </div>
          )}

          <div>
            <Label className="text-gray-400">Send Confirmation To (multiple emails) *</Label>
            <div className="flex gap-2 mt-1">
              <Input
                type="email"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addEmail(); } }}
                placeholder="Type email and press Enter or +"
                className="bg-[#111111] border-[#262626]"
                data-testid="confirm-meeting-email-input"
              />
              <Button type="button" onClick={addEmail} className="bg-[#262626] hover:bg-[#333] px-3 shrink-0" data-testid="add-email-btn">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            {emails.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2" data-testid="email-chips">
                {emails.map((em) => (
                  <span key={em} className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-[#FF6B35]/15 text-[#FF6B35] text-xs rounded-full">
                    {em}
                    <button type="button" onClick={() => setEmails(emails.filter((e) => e !== em))} className="hover:text-white">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            <p className="text-[10px] text-gray-600 mt-1.5">Everyone added will receive the confirmation. You're auto-CC'd on the first email.</p>
          </div>

          <div>
            <Label className="text-gray-400">Notes (optional)</Label>
            <Textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="e.g., Please bring the plot documents"
              className="bg-[#111111] border-[#262626]"
            />
          </div>

          <div className="sticky bottom-0 -mx-6 -mb-6 mt-2 px-6 py-4 max-sm:pb-[calc(1rem+env(safe-area-inset-bottom))] bg-[#1A1A1A] border-t border-[#262626] flex justify-end gap-2">
            <Button variant="outline" className="flex-1 sm:flex-none" onClick={() => onOpenChange(false)} data-testid="confirm-meeting-cancel-btn">Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting} className="flex-1 sm:flex-none bg-[#FF6B35] hover:bg-[#FF6B35]/90" data-testid="confirm-meeting-send-btn">
              <MailCheck className="w-4 h-4 mr-2" />
              {submitting ? "Sending..." : "Send Confirmation"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
